import { useEffect, useState } from 'react';
import useApi from 'hooks/useApi';

/**
 * Whether the signed-in user may manage legal documents and consent settings.
 * Decided by the server (`GET legal/access`, `!role.client`) — never by matching role
 * names here — and only a UI gate: every legal admin route re-checks it. Any failure
 * reads as "no access".
 */
export default function useLegalAccess(): { isSuperAdmin: boolean; loading: boolean } {
  const { user, getLegalAccess } = useApi();
  // Keyed by user so signing in as someone else in the same tab re-asks.
  const userKey = user?.uid ?? user?.id ?? '';
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userKey) {
      setIsSuperAdmin(false);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    getLegalAccess()
      .then((response: any) => {
        if (!cancelled) setIsSuperAdmin(response?.success !== false && response?.data?.isSuperAdmin === true);
      })
      .catch(() => {
        if (!cancelled) setIsSuperAdmin(false);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [userKey, getLegalAccess]);

  return { isSuperAdmin, loading };
}
